'use client';

import { useEffect } from 'react';
import { useSession } from 'next-auth/react';
import { track } from '@/lib/analytics';
import { trackEvent } from '@/lib/tracking';

type Props = {
  personId: number | string;
  ownerUserId: string | null;
};

// Records a profile view for authenticated AND guest viewers. Guests are
// counted server-side under a keyed daily-rotating hash (no identity stored,
// never shown in the named-viewers list); most profile traffic is logged-out,
// so gating this on auth starved who-viewed-you and the digest ViewerCount.
// Server-side throttles to one record per viewer per profile per hour
// (ProfileViewController.RecordView) and rejects self-views.
export default function ProfileViewBeacon({ personId, ownerUserId }: Props) {
  const { data: session, status } = useSession();

  useEffect(() => {
    if (status === 'loading') return; // wait to know if authed, avoids double-fire
    if (!personId) return;
    const viewerUserId = (session as any)?.user?.id || null;
    if (viewerUserId && ownerUserId && viewerUserId === ownerUserId) return;

    const url = `/api/proxy/api/profile/${encodeURIComponent(String(personId))}/view`;
    fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })
      .then((res) => {
        if (res.ok) {
          track('profile_view_recorded', { personId: String(personId) });
          trackEvent('profile_view_recorded', { subject_person_id: String(personId) });
        }
      })
      .catch(() => {});
  }, [personId, ownerUserId, status, session]);

  return null;
}
